// A part's MIDI is stored as parsed JSON rather than as a binary .mid file. The JSON carries
// every note event the file had, needs no object storage, and — the reason it's worth doing —
// can be transposed in place so the MIDI follows the song when the key changes.
//
// Shape persisted on a part as `part.midi`:
//   { name, bpm, timeSignature: [4, 4], duration, sourceKey, tracks: [{ name, instrument, notes }] }
// `sourceKey` is the song key the clip was uploaded against. It anchors the MIDI to a pitch of
// its own rather than to whatever song.key happens to say now, so editing the song's key later
// re-derives the offset correctly instead of silently relabelling the same audio.
// Notes hold the MIDI number, not a note name, so transposing is arithmetic instead of string
// surgery on sharps and flats. Times are absolute seconds, as @tonejs/midi reports them.
//
// A note carries two lengths, because the pedal makes them different things:
//   duration - how long the key itself was held, exactly as the file records it
//   sustain  - how long the note actually rings once the pedal is taken into account, present
//              only on notes the pedal extended
// The synth plays `sustain`; the on-screen keyboard highlights `duration`, so a key lifts under
// your finger while the note goes on sounding — and a repeated note is visibly restruck rather
// than looking like one long press.

import { getKeyRootDistance } from './keyDistance';

const MIN_MIDI_NOTE = 0;
const MAX_MIDI_NOTE = 127;

// Four decimals is well below anything audible and keeps the stored document small
const round = (value) => Math.round(value * 10000) / 10000;

const DEFAULT_BPM = 120;
const DEFAULT_TIME_SIGNATURE = [4, 4];

// The sustain pedal is recorded as control change 64, separately from the notes themselves: a
// note-off is still written the moment the key comes up, so a pedalled passage parses as a run of
// short notes plus a pedal-down event that nothing looks at — which is why an uploaded clip came
// out sounding nothing like it was played. The pedal is folded into note durations here at parse
// time rather than stored as events and honoured during playback, so a stored clip stays a flat
// list of notes: transposition, the size cap, the summary counts and the keyboard highlighting all
// already work against that shape and need no say in the pedal at all.
const SUSTAIN_CC = 64;

// @tonejs/midi normalises control change values to 0-1; the spec's threshold for the damper
// counting as down is 64 of 127. Half-pedalling counts as fully down — a controller sweeping the
// range is still asking for the note to ring, and a sampled piano has no half-damper to offer it.
const PEDAL_DOWN = 0.5;

/**
 * The spans of time the pedal is held down on a track, in order and non-overlapping.
 *
 * @param {object} track - A parsed @tonejs/midi track
 * @param {number} clipEnd - Where the file stops, for a pedal that is never lifted
 * @returns {Array<{ from: number, to: number }>} Absolute seconds, as the note times are
 */
function getPedalHolds(track, clipEnd) {
  const events = track.controlChanges?.[SUSTAIN_CC] || [];
  const holds = [];
  let downAt = null;

  for (const { time, value } of [...events].sort((a, b) => a.time - b.time)) {
    if (value >= PEDAL_DOWN) {
      // Pushing an already-held pedal further down doesn't start a second hold
      if (downAt === null) downAt = time;
    } else if (downAt !== null) {
      holds.push({ from: downAt, to: time });
      downAt = null;
    }
  }

  // A recording that ends with the foot still down rings to the end of the clip rather than having
  // the hold dropped altogether. Nothing in the file says how much longer it was meant to last,
  // and the player's release tail covers the difference.
  if (downAt !== null) holds.push({ from: downAt, to: clipEnd });

  return holds;
}

/**
 * Work out how long each note rings once the pedal is taken into account.
 *
 * Written to a separate `sustain` rather than over the note's own duration: the key came up when
 * the file says it did, and the keyboard on screen has to keep showing that. Only the sound goes
 * on longer.
 *
 * What decides it is the pedal's state at the moment of note-off, which is how a damper behaves:
 * pressing the pedal after the key is already up doesn't recapture a note that has stopped, and
 * lifting it before the key comes up leaves the note to end on its own.
 */
function applySustain(notes, holds) {
  if (holds.length === 0) return notes;

  const sustained = notes.map((note) => {
    const end = note.time + note.duration;
    const hold = holds.find(({ from, to }) => from <= end && end < to);

    return hold ? { ...note, sustain: hold.to - note.time } : note;
  });

  // Striking a key that is still ringing under the pedal stops the first note — one string, one
  // damper. Without this the overlap is audible: a Sampler will happily play two copies of the
  // same pitch at once, so a repeated note flams against itself instead of being restruck.
  //
  // Never cut shorter than the key was actually held: in a file whose notes already overlap, the
  // restrike can land while the first key is still down, and the note has to outlast its own press.
  const ringing = new Map();

  for (const note of [...sustained].sort((a, b) => a.time - b.time)) {
    const previous = ringing.get(note.midi);

    if (previous && previous.sustain > note.time - previous.time) {
      previous.sustain = Math.max(previous.duration, note.time - previous.time);
    }

    ringing.set(note.midi, note);
  }

  return sustained;
}

/**
 * Trim a parsed @tonejs/midi document down to what playback and transposition actually need.
 * The raw toJSON() output also carries pitch bends, the full header, and the control changes that
 * applySustain has already folded into the note durations by this point.
 */
function toMidiJson(midi, name) {
  const [firstTempo] = midi.header.tempos;
  const [firstTimeSignature] = midi.header.timeSignatures;

  return {
    name: name || midi.header.name || '',
    bpm: firstTempo ? Math.round(firstTempo.bpm) : DEFAULT_BPM,
    timeSignature: firstTimeSignature ? firstTimeSignature.timeSignature : DEFAULT_TIME_SIGNATURE,
    // Still the file's own length: a hold that outlives the last note-off is clamped to the end of
    // the clip, so sustain can't stretch a part past where it stopped
    duration: round(midi.duration),
    tracks: midi.tracks
      .filter((track) => track.notes.length > 0)
      .map((track) => ({
        name: track.name || '',
        instrument: track.instrument?.name || '',
        notes: applySustain(
          track.notes.map((note) => ({
            midi: note.midi,
            time: note.time,
            duration: note.duration,
            velocity: note.velocity,
          })),
          getPedalHolds(track, midi.duration),
        ).map((note) => ({
          midi: note.midi,
          time: round(note.time),
          duration: round(note.duration),
          velocity: round(note.velocity),
          // Carried only by notes the pedal actually held on, so an unpedalled clip is stored at
          // exactly the size it always was
          ...(note.sustain > note.duration ? { sustain: round(note.sustain) } : {}),
        })),
      })),
  };
}

/**
 * Parse a binary .mid file into the stored JSON shape.
 * @tonejs/midi is imported on demand so it stays out of the main bundle.
 *
 * @param {ArrayBuffer} arrayBuffer - Raw contents of the .mid file
 * @param {string} name - Original filename, kept for display
 * @returns {Promise<object>} The parsed MIDI JSON
 */
export async function parseMidiFile(arrayBuffer, name = '') {
  const { Midi } = await import('@tonejs/midi');

  return toMidiJson(new Midi(arrayBuffer), name);
}

/**
 * Shift every note by a number of semitones. Notes that would fall outside the MIDI range are
 * clamped to the nearest valid note rather than dropped, so nothing silently disappears.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @param {number} semitones - Positive to transpose up, negative down
 * @returns {object} A new MIDI JSON — the input is not mutated
 */
export function transposeMidiJson(midiJson, semitones) {
  if (!midiJson || !semitones) return midiJson;

  return {
    ...midiJson,
    tracks: midiJson.tracks.map((track) => ({
      ...track,
      notes: track.notes.map((note) => ({
        ...note,
        midi: Math.min(MAX_MIDI_NOTE, Math.max(MIN_MIDI_NOTE, note.midi + semitones)),
      })),
    })),
  };
}

// A clip's `octaveDouble`, saved with it: which octave every note is mirrored into as it plays
export const OCTAVE_DOUBLINGS = { below: 'below', above: 'above' };

/**
 * Mirror every note an octave below or above, so a line played with one hand — a horn figure —
 * sounds as if played in octaves with both. Derived on every play; the stored notes are untouched.
 * A mirrored note that would leave the MIDI range is dropped, since clamping would stack it on the
 * original.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @returns {object} A new MIDI JSON, or the same one when the clip isn't doubled
 */
export function doubleOctave(midiJson) {
  const shift = { below: -12, above: 12 }[midiJson?.octaveDouble];

  if (!shift) return midiJson;

  const inRange = (note) => note.midi + shift >= MIN_MIDI_NOTE && note.midi + shift <= MAX_MIDI_NOTE;

  return {
    ...midiJson,
    tracks: midiJson.tracks.map((track) => ({
      ...track,
      notes: [...track.notes, ...track.notes.filter(inRange).map((note) => ({ ...note, midi: note.midi + shift }))],
    })),
  };
}

// Legato overlap a single line is still allowed: this long, or a quarter of the earlier note if shorter
const LEGATO_OVERLAP_SECONDS = 0.06;
const LEGATO_OVERLAP_SHARE = 0.25;

/**
 * Whether a clip plays one note at a time. A note may start a little before the last one ends, as
 * legato playing does; one that starts well inside it is a chord or a second hand. The pedal's
 * ring isn't counted, only the keys held down.
 */
export function isSingleLine(midiJson) {
  let held = null;

  for (const note of flattenMidiNotes(midiJson)) {
    if (held) {
      const allowed = Math.min(LEGATO_OVERLAP_SECONDS, held.duration * LEGATO_OVERLAP_SHARE);

      if (held.time + held.duration - note.time > allowed) return false;
    }

    if (!held || note.time + note.duration > held.time + held.duration) held = note;
  }

  return true;
}

/**
 * How far a clip has to move to sound in the key currently on screen.
 *
 * Two independent shifts add up here:
 *
 *   1. The clip against the song. A clip carries the key it was uploaded in, so editing the
 *      song's key afterwards re-derives this rather than silently relabelling the same audio.
 *      A clip with no sourceKey predates that field and is taken to be in the song's own key,
 *      which is what it was uploaded as.
 *   2. The song against the screen. This is passed in as a count rather than worked out from
 *      the key names — View already accumulates it exactly, and a count can't be misread.
 *
 * Derived on every play rather than written back to the stored clip. transposeMidiJson clamps
 * notes that fall outside the MIDI range instead of dropping them, which is right for playback
 * but means the operation doesn't round trip: persisting a transpose up and then back down would
 * leave anything that hit the ceiling permanently squashed. Keeping the stored clip pristine and
 * re-deriving the offset also mirrors how chords already work.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @param {string} songKey - The song's own key, before any on-screen transposition
 * @param {number} transposeSemitones - How far the song has been transposed on screen
 * @returns {number} Semitones to shift the clip by
 */
export function getMidiTransposition(midiJson, songKey, transposeSemitones = 0) {
  const sourceKey = midiJson?.sourceKey;
  const clipOffset = sourceKey && songKey ? getKeyRootDistance(sourceKey, songKey) : 0;

  return clipOffset + (transposeSemitones || 0);
}

/**
 * Shift for a clip previewed against a key that is itself being edited, as in the song form.
 *
 * The counterpart to getMidiTransposition. There the song's key is fixed and the shift arrives
 * as a count; here there is no on-screen transposition at all — the key field is the target.
 *
 * The distance is measured between the two roots, so switching a song from C to Cm leaves the
 * clip where it is: the tonic hasn't moved, only the mode the song is written in, and the clip
 * is a fixed recording either way.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @param {string} targetKey - The key the clip should sound in, i.e. what the form now says
 * @param {string} fallbackSourceKey - Where the clip is taken to be when it carries no
 *   sourceKey of its own: the key the song was loaded with, which is what it was uploaded as
 * @returns {number} Semitones to shift the clip by
 */
export function getMidiTranspositionToKey(midiJson, targetKey, fallbackSourceKey = '') {
  const sourceKey = midiJson?.sourceKey || fallbackSourceKey;

  if (!sourceKey || !targetKey) return 0;

  return getKeyRootDistance(sourceKey, targetKey);
}

/**
 * Every note across every track, ordered in time — what the player schedules.
 */
export function flattenMidiNotes(midiJson) {
  if (!midiJson?.tracks) return [];

  return midiJson.tracks.flatMap((track) => track.notes || []).sort((a, b) => a.time - b.time);
}

// A downbeat played this far ahead of the bar line still counts as on it, not as a pickup
const EARLY_TOLERANCE_BEATS = 0.125;

// A note moved to before 0 starts at 0 and still ends where it did
const shiftNote = (note, shift) => {
  const time = note.time - shift;

  if (time >= 0) return { ...note, time: round(time) };

  return {
    ...note,
    time: 0,
    duration: round(Math.max(0, note.duration + time)),
    ...(note.sustain ? { sustain: round(Math.max(0, note.sustain + time)) } : {}),
  };
};

// A clip's `trimLeadIn`, saved with it: how much of the silence it opens with is dropped
export const LEAD_IN_TRIMS = { bars: 'bars', silence: 'silence' };

// How far a clip moves earlier: its whole empty bars, or everything up to the first note
const getLeadInShift = (midiJson, firstNote, beatsPerBar) => {
  if (midiJson.trimLeadIn === LEAD_IN_TRIMS.silence) return firstNote.time;

  const secondsPerBeat = 60 / (midiJson.bpm || DEFAULT_BPM);
  const secondsPerBar = secondsPerBeat * beatsPerBar;
  const emptyBars = Math.floor((firstNote.time + EARLY_TOLERANCE_BEATS * secondsPerBeat) / secondsPerBar);

  return emptyBars * secondsPerBar;
};

/**
 * Drop the whole empty bars a clip opens with — usually a DAW count-in recorded along with the
 * part — so its first bar with notes is bar 1. A pickup inside that bar is kept, unless the clip
 * is set to drop all its leading silence (`trimLeadIn: 'silence'`), when the first note lands on 0.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @param {number} beatsPerBar - How many beats make a bar
 * @returns {object} A new MIDI JSON, or the same one when nothing opens empty
 */
export function trimLeadInBars(midiJson, beatsPerBar = 4) {
  const [firstNote] = flattenMidiNotes(midiJson);

  if (!firstNote || !beatsPerBar) return midiJson;

  const shift = getLeadInShift(midiJson, firstNote, beatsPerBar);

  if (shift <= 0) return midiJson;

  return {
    ...midiJson,
    duration: round(Math.max(0, midiJson.duration - shift)),
    tracks: midiJson.tracks.map((track) => ({
      ...track,
      notes: track.notes.map((note) => shiftNote(note, shift)),
    })),
  };
}

/**
 * Re-time a clip to another tempo, so it can be played against something laid out at that one.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @param {number} toBpm - The tempo to play it at
 * @returns {object} A new MIDI JSON, or the same one when the tempo already matches
 */
export function rescaleMidiJson(midiJson, toBpm) {
  const fromBpm = midiJson?.bpm || DEFAULT_BPM;

  if (!midiJson || !toBpm || toBpm === fromBpm) return midiJson;

  const scale = fromBpm / toBpm;
  const scaleNote = (note) => ({
    ...note,
    time: round(note.time * scale),
    duration: round(note.duration * scale),
    ...(note.sustain ? { sustain: round(note.sustain * scale) } : {}),
  });

  return {
    ...midiJson,
    bpm: toBpm,
    duration: round(midiJson.duration * scale),
    tracks: midiJson.tracks.map((track) => ({ ...track, notes: track.notes.map(scaleNote) })),
  };
}

/**
 * Move a whole clip later, for a melody that belongs partway into a chart rather than at its top —
 * or earlier, for a pickup that comes in before it. Notes moved before 0 keep their negative times.
 *
 * @param {object} midiJson - MIDI JSON in the stored shape
 * @param {number} seconds - How much later to start it; negative is earlier
 * @returns {object} A new MIDI JSON, or the same one when it doesn't move
 */
export function shiftMidiJson(midiJson, seconds) {
  if (!midiJson || !seconds) return midiJson;

  return {
    ...midiJson,
    duration: round(midiJson.duration + seconds),
    tracks: midiJson.tracks.map((track) => ({
      ...track,
      notes: track.notes.map((note) => ({ ...note, time: round(note.time + seconds) })),
    })),
  };
}

/**
 * How many beats before its part's first bar a clip comes in: its `pickupBars`, then its
 * `pickupBeats` nudge. Both are absent on a clip that comes in with its part.
 */
export function getPickupBeats(midiJson, beatsPerBar) {
  return (midiJson?.pickupBars || 0) * beatsPerBar + (midiJson?.pickupBeats || 0);
}

/**
 * Beats per bar from a clip's own time signature, as @tonejs/midi reports it.
 */
export function getMidiBeatsPerBar(midiJson) {
  return midiJson?.timeSignature?.[0] || DEFAULT_TIME_SIGNATURE[0];
}

// The JSON is stored inside the song document and travels in every song request, so a single
// part's clip is capped. At ~61 bytes per note this is roughly 4,000 notes — far denser than
// any real part — and leaves room for several MIDI parts within the API's 2mb body limit.
export const MAX_MIDI_JSON_BYTES = 256 * 1024;

// A part is a section of a song, not the whole recording — a clip longer than this is almost
// certainly the wrong file attached to the wrong place.
export const MAX_MIDI_DURATION_SECONDS = 60;

/**
 * Size of the clip as it will be stored and sent.
 */
export function getMidiJsonBytes(midiJson) {
  if (!midiJson) return 0;

  return new Blob([JSON.stringify(midiJson)]).size;
}

/**
 * A clip's length as m:ss.
 */
export function formatMidiDuration(seconds) {
  const total = Math.round(seconds || 0);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * How many notes the clip holds — what makes "empty MIDI" visible to the user.
 */
export function countMidiNotes(midiJson) {
  if (!midiJson?.tracks) return 0;

  return midiJson.tracks.reduce((total, track) => total + (track.notes?.length || 0), 0);
}

/**
 * Does this part have MIDI worth offering playback for?
 */
export function hasMidi(part) {
  return Boolean(part?.midi?.tracks?.some((track) => track.notes?.length > 0));
}
